import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import { Presentation } from './presentation';
import { createViaReact } from '../../../services/people';
import { getCsrfToken } from '../../../libs/csrfToken';
import { RecruitmentRegisterModalProps as Props, RecruitmentRegisterModalState } from './types';
import { recordForDropout } from '../../../services/personConnectRecruitments';

export const Component: FunctionComponent<Props> = (props) => {
  const [state, setState] = useState<RecruitmentRegisterModalState>({
    isNextSection: false,
    submitText: '登録 / 次へ',
    occupation: '',
    preferredOccupation: '',
    isHeightTooShort: false,
    formState: { scoutFlag: '1', agreement: false },
    dataEventRegister: 'click',
    dataCategoryRegister: 'recruitmentcontent_register_popup',
    dataValueRegister: 'register'
  });
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    const targetWidth = 844;
    const currentHeight = window.innerHeight;
    if (currentHeight < targetWidth) {
      setState(prev => ({ ...prev, isHeightTooShort: true }));
    }
  }, []);

  const handleOauthsLinkClick = (provider: string) => {
    sessionStorage.setItem("route_type", "recruitment_new");
    sessionStorage.setItem("route_recruitment_id", String(props.recruitmentId ?? ''));
    window.location.href = `/oauths/${provider}?redirect_url=${props.redirectUrl}`;
  };

  const onChanged = (name: string, value: any) => {
    setState(prev => ({
      ...prev,
      formState: {
        ...prev.formState,
        [name]: value,
      },
      occupation: name === 'occupation' ? value : prev.occupation,
      preferredOccupation: name === 'preferredOccupation' ? value : prev.preferredOccupation,
    }));
  };

  const onClicked = () => {
    setState(prev => ({
      ...prev,
      formState: {
        ...prev.formState,
        agreement: !prev.formState.agreement,
      },
    }));
  };

  const handleOpenNextSection = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    if ((!state.formState.email || !state.formState.password) && !state.isNextSection) {
      alert('メールアドレスとパスワードを入力してください。');
    } else if (state.isNextSection) {
      handleSubmit(e);
    } else {
      setState(prev => ({
        ...prev,
        isNextSection: true,
        submitText: '登録してエントリーする',
        dataEventRegister: 'click',
        dataCategoryRegister: 'recruitmentcontent_register_popup',
        dataValueRegister: 'actual_register'
      }));
      setTimeout(() => {
        const nextElement = document.getElementById('next');
        if (nextElement) {
          nextElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const isValid = (e: React.MouseEvent) => {
    const form = document.getElementById('register-modal');
    if (!form) return false;

    const necessaryItems = (): [string, string][] => {
      if (state.occupation === '15') {
        return [['email', 'メールアドレス'], ['password', 'パスワード'], ['firstName', '氏名（姓）'], ['lastName', '氏名（名）'], ['year', '年'], ['month', '月'], ['day', '日'], ['occupation', '直近の職種'], ['preferredOccupation', '希望の職種'], ['prefecture', '希望勤務地'], ['graduationYear', '卒業予定年'], ['agreement', '利用規約']];
      } else {
        return [['email', 'メールアドレス'], ['password', 'パスワード'], ['firstName', '氏名（姓）'], ['lastName', '氏名（名）'], ['year', '年'], ['month', '月'], ['day', '日'], ['occupation', '直近の職種'], ['preferredOccupation', '希望の職種'], ['prefecture', '希望勤務地'], ['agreement', '利用規約']];
      }
    };
    const alertText: string[] = [];
    type FormKey = keyof typeof state.formState;
    for (const [key, label] of necessaryItems()) {
      const formKey = key as FormKey;
      const value = state.formState[formKey];
      if (value === undefined || value === '') {
        alertText.push(label);
      }
      if (formKey === 'agreement') {
        if (!value) {
          alertText.push(label);
        }
      }
    }
    if (alertText.length > 0) {
      alert(`${alertText.join(', ')}は必須項目です。`);
      e.preventDefault();
      return false;
    }
    if (form.querySelectorAll(':invalid').length > 0) {
      return false;
    }
    e.preventDefault();
    return true;
  };

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    if (!isValid(e)) return;

    window.dataLayer.push({
      event: 'signup'
    });

    const params = {
      people_new_form: {
        email: state.formState.email,
        password: state.formState.password,
        first_name: state.formState.firstName,
        last_name: state.formState.lastName,
        birthday_1i: state.formState.year,
        birthday_2i: state.formState.month,
        birthday_3i: state.formState.day,
        occupation_id: state.occupation,
        occupation_detail_id: state.occupation !== '15' ? state.formState.occupationDetail : '',
        preferred_occupation_id: state.preferredOccupation,
        preferred_occupation_detail_id: state.formState.preferredOccupationDetail || '',
        prefecture_id: state.formState.prefecture,
        graduated_at: state.occupation === '15' ? state.formState.graduationYear : '',
        scout_flag: state.formState.scoutFlag === '1',
        agreement: state.formState.agreement
      }
    };
    try {
      const response = await createViaReact(params, getCsrfToken());
      if (response.message === 'ok') {
        props.handleFlash(true, 'success', '新規登録が完了しました');
        props.handleIsLogin();
        setIsLoginSuccess(true);
      } else {
        props.handleFlash(true, 'danger', response.message || '新規登録に失敗しました');
      }
    } catch (error) {
      console.error(error);
      props.handleFlash(true, 'danger', '新規登録に失敗しました');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const useLoginSuccessEffect = (isLoginSuccess: boolean) => {
    useEffect(() => {
      if (!isLoginSuccess || !props.recruitmentId) return;
      const recruitmentId = props.recruitmentId;

      const run = async () => {
        try {
          const response = await recordForDropout(recruitmentId, getCsrfToken(), 2); // 新規登録からエントリー
          if (response.status === 'success') {
            props.handleRegisterModalOpen();
            props.handleConnectModalOpen();
          } else if (response.status === 'error') {
            props.handleFlash(true, 'danger', response.errormessage ?? 'エラーが発生しました');
          }
        } catch (error) {
          console.error(error);
          alert(`エラーが発生しました：${error}`);
        }
      };

      run();
    }, [isLoginSuccess]);
  };

  useLoginSuccessEffect(isLoginSuccess);

  return (
    <Presentation
      {...props}
      state={state}
      isSubmitting={isSubmitting}
      onChanged={onChanged}
      onClicked={onClicked}
      handleOpenNextSection={handleOpenNextSection}
      handleOauthsLinkClick={handleOauthsLinkClick}
    />
  );
};
